import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as inventoryApi from '../api/inventoryApi';
import { Card, EmptyState, ErrorBanner, Modal, Skeleton, Spinner, TableSkeleton } from '../components/ui';
import { IconAlert } from '../components/icons';
import {
  InventoryAlert,
  InventoryCostLayer,
  InventoryItem,
  InventoryTransaction,
  RestockWaitingGroup,
} from '../types/inventory';
import {
  capitalize,
  formatDate,
  formatDateTime,
  formatINRPrecise,
  formatMl,
  formatMlSigned,
  formatMlValue,
  shortId,
} from '../utils/format';

// Unnumbered oils sort behind every numbered one, in both directions: ascending
// they trail the shelf, descending they lead it. Same rule the server orders by
// (COALESCE(manufacturer_code, 9999)), so the table and the API agree.
const UNNUMBERED = Number.MAX_SAFE_INTEGER;

function codeOf(item: InventoryItem): number {
  return item.manufacturer_code ?? UNNUMBERED;
}

// The shelf number, shown wherever an oil is identified. Gold and monospaced so
// a column of them reads as numbers to scan rather than as prose.
function OilCode({ code }: { code: number | null }) {
  if (code === null) return <span className="text-sub">—</span>;
  return <span className="font-mono font-semibold text-gold">{code}</span>;
}

function StockStatus({ status }: { status: InventoryItem['status'] }) {
  if (status === 'out')
    return (
      <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-0.5 text-xs font-medium text-red-600 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-300">
        Out of Stock
      </span>
    );
  if (status === 'low')
    return (
      <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300">
        Low Stock
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
      Healthy
    </span>
  );
}

// Customers who asked to be told when this oil is back. Shown only where it
// changes a decision — next to stock that is low or out — so a healthy row
// doesn't carry a badge nobody needs to act on.
function WaitingBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="ml-2 inline-flex items-center rounded-full border border-gold/40 bg-gold/10 px-2 py-0.5 text-[11px] font-medium text-gold"
      title={`${count} customer${count === 1 ? '' : 's'} will be notified when this is restocked`}
    >
      {count} waiting
    </span>
  );
}

// A batch is depleted once costing has drawn everything out of it — it stays in
// the list as history, but no future formula will be priced from it.
function CostLayers({ layers, loading }: { layers?: InventoryCostLayer[]; loading: boolean }) {
  if (loading || !layers) return <TableSkeleton rows={3} />;
  if (layers.length === 0) {
    return (
      <p className="text-sm text-sub">
        No cost batches recorded. The next restock will open the first one.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-sub">
        Cost batches — consumed oldest first
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-edge">
            <th className="th">Received date</th>
            <th className="th text-right">Batch size</th>
            <th className="th text-right">Remaining</th>
            <th className="th text-right">Cost/ml</th>
            <th className="th">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-edge">
          {layers.map((layer) => {
            const depleted = layer.remaining_ml <= 0;
            return (
              <tr key={layer.id} className={depleted ? 'opacity-60' : undefined}>
                <td className="td text-sub">{formatDate(layer.received_at)}</td>
                <td className="td text-right">{formatMl(layer.quantity_ml)}</td>
                <td className="td text-right">
                  {depleted ? (
                    <span className="inline-flex items-center rounded-full border border-edge bg-edge/40 px-2.5 py-0.5 text-xs font-medium text-sub">
                      DEPLETED
                    </span>
                  ) : (
                    <span className="font-medium">{formatMl(layer.remaining_ml)}</span>
                  )}
                </td>
                <td className="td text-right">{formatINRPrecise(layer.cost_per_ml)}</td>
                <td className="max-w-[240px] truncate px-4 py-3 text-sub" title={layer.notes ?? ''}>
                  {layer.notes ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The shelf reference sheet — the oil list in manufacturer order, on paper.
 *
 * Deliberately its own document rather than a print stylesheet over this page:
 * what goes on the bench next to the bottles is the numbers, the names and the
 * stock, black on white. The admin's dark theme, the nav and the action buttons
 * are all noise there, and a print stylesheet would have to fight every one of
 * them.
 *
 * Returns false when the popup was blocked, which is the only failure the
 * caller can do anything about.
 */
function printOilList(items: InventoryItem[]): boolean {
  const win = window.open('', '_blank');
  if (!win) return false;

  const rows = [...items]
    .sort((a, b) => codeOf(a) - codeOf(b) || a.oil_name.localeCompare(b.oil_name))
    .map(
      (item) => `
        <tr>
          <td class="num">${item.manufacturer_code ?? '—'}</td>
          <td>${escapeHtml(item.oil_name)}</td>
          <td class="num">${formatMlValue(item.quantity_ml)}</td>
          <td class="num">${formatMlValue(item.reorder_level_ml)}</td>
          <td class="num">${
            item.unit_cost_per_ml ? escapeHtml(formatINRPrecise(item.unit_cost_per_ml)) : '—'
          }</td>
        </tr>`
    )
    .join('');

  win.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Manufacturer Oil Reference</title>
    <style>
      @page { margin: 14mm; }
      body {
        font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
        color: #000; background: #fff; margin: 0; padding: 24px;
      }
      h1 { font-size: 16px; letter-spacing: .08em; text-transform: uppercase; margin: 0 0 4px; }
      p.meta { font-size: 11px; color: #444; margin: 0 0 18px; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { border: 1px solid #000; padding: 5px 8px; text-align: left; }
      th { background: #eee; font-weight: 700; }
      /* Numbers get the monospace column so figures line up down the page. */
      .num { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; text-align: right; }
      td.num:first-child, th.num:first-child { text-align: center; width: 48px; }
      /* Repeat the header on every sheet — this list runs past one page. */
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
    </style>
  </head>
  <body>
    <h1>The Perfume Culture — Manufacturer Oil Reference</h1>
    <p class="meta">Generated ${escapeHtml(formatDateTime(new Date().toISOString()))} · ${
      items.length
    } oils</p>
    <table>
      <thead>
        <tr>
          <th class="num">#</th>
          <th>Oil Name</th>
          <th class="num">Stock (ml)</th>
          <th class="num">Reorder Level (ml)</th>
          <th class="num">Unit Cost</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  </body>
</html>`);
  win.document.close();

  // Give the new document a beat to lay out before the dialog freezes it —
  // printing immediately can capture a blank first page in some browsers.
  win.setTimeout(() => win.print(), 500);
  return true;
}

export default function InventoryPage() {
  const [tab, setTab] = useState<'stock' | 'transactions'>('stock');
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [waiting, setWaiting] = useState<RestockWaitingGroup[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [txLoaded, setTxLoaded] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  // Restock modal
  const [restockItem, setRestockItem] = useState<InventoryItem | null>(null);
  const [restockQty, setRestockQty] = useState('');
  const [restockCost, setRestockCost] = useState('');
  const [restockNotes, setRestockNotes] = useState('');
  const [restocking, setRestocking] = useState(false);

  // Expanded cost-layer rows, keyed by oil name. Layers are fetched on first
  // expand and cached — an oil's batch history only changes when it is restocked.
  const [expandedOil, setExpandedOil] = useState('');
  const [layersByOil, setLayersByOil] = useState<Record<string, InventoryCostLayer[]>>({});
  const [layersLoading, setLayersLoading] = useState('');

  // Edit-cost modal
  const [costItem, setCostItem] = useState<InventoryItem | null>(null);
  const [costValue, setCostValue] = useState('');
  const [savingCost, setSavingCost] = useState(false);

  // Reorder-settings modal. Two admin-set values, neither of which the system
  // acts on by itself:
  //   reorder_level_ml — alert threshold. Stock at or below it raises the LOW
  //     STOCK warning. Set from the lead time needed to reorder before running
  //     out. This change only makes the threshold editable; what triggers an
  //     alert is unchanged.
  //   reorder_qty_ml   — suggested reorder quantity: how much to order from the
  //     supplier when restocking, from typical usage and supplier bottle sizes.
  //     Purely a reminder — it places no orders and touches no order flow.
  const [reorderItem, setReorderItem] = useState<InventoryItem | null>(null);
  const [reorderLevel, setReorderLevel] = useState('');
  const [reorderQty, setReorderQty] = useState('');
  // The manufacturer's shelf number, edited alongside the reorder knobs — one
  // modal, one save. Empty string means "no number", which is a real value here
  // (it clears the code), not a skipped field.
  const [reorderCode, setReorderCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [savingReorder, setSavingReorder] = useState(false);
  const [notice, setNotice] = useState('');

  // Stock table view state. Sorted by shelf number by default — the order the
  // oils physically stand in — rather than by stock level; the summary cards and
  // the alerts list below are where low stock is read off.
  const [search, setSearch] = useState('');
  const [codeSortAsc, setCodeSortAsc] = useState(true);

  const [resolvingId, setResolvingId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inv, al] = await Promise.all([inventoryApi.getInventory(), inventoryApi.getAlerts()]);
      setItems(inv);
      setAlerts(al);

      // Waiting lists are supplementary — a failure here must not take the whole
      // stock table down with it, so it's fetched separately and swallowed.
      try {
        setWaiting(await inventoryApi.getRestockWaiting());
      } catch {
        setWaiting([]);
      }
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      setTransactions(await inventoryApi.getTransactions(50));
      setTxLoaded(true);
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setTxLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'transactions' && !txLoaded) loadTransactions();
  }, [tab, txLoaded, loadTransactions]);

  // Load an oil's cost layers on demand; cached after the first expand.
  const toggleLayers = async (oilName: string) => {
    if (expandedOil === oilName) {
      setExpandedOil('');
      return;
    }
    setExpandedOil(oilName);
    if (layersByOil[oilName]) return;

    setLayersLoading(oilName);
    try {
      const layers = await inventoryApi.getCostLayers(oilName);
      setLayersByOil((prev) => ({ ...prev, [oilName]: layers }));
    } catch (err) {
      setActionError(apiErrorMessage(err));
      setExpandedOil('');
    } finally {
      setLayersLoading('');
    }
  };

  const handleRestock = async () => {
    if (!restockItem) return;
    const qty = Number(restockQty);
    const cost = Number(restockCost);
    if (!qty || qty <= 0) {
      setActionError('Enter a quantity greater than 0');
      return;
    }
    if (restockCost.trim() === '' || !Number.isFinite(cost) || cost < 0) {
      setActionError('Enter the cost per ml for this batch');
      return;
    }
    setRestocking(true);
    setActionError('');
    try {
      await inventoryApi.restockOil(restockItem.oil_name, qty, cost, restockNotes.trim());
      // This restock opened a new cost layer, so the cached batch list for this
      // oil is now stale.
      setLayersByOil((prev) => {
        const next = { ...prev };
        delete next[restockItem.oil_name];
        return next;
      });
      setRestockItem(null);
      setRestockQty('');
      setRestockCost('');
      setRestockNotes('');
      setTxLoaded(false);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setRestocking(false);
    }
  };

  const handleSaveCost = async () => {
    if (!costItem) return;
    const cost = Number(costValue);
    if (costValue.trim() === '' || !Number.isFinite(cost) || cost < 0) {
      setActionError('Enter a cost of 0 or more');
      return;
    }
    setSavingCost(true);
    setActionError('');
    try {
      await inventoryApi.updateOilCost(costItem.oil_name, cost);
      setCostItem(null);
      setCostValue('');
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSavingCost(false);
    }
  };

  const handleSaveReorder = async () => {
    if (!reorderItem) return;

    const settings: {
      reorder_level_ml?: number;
      reorder_qty_ml?: number;
      manufacturer_code?: number | null;
    } = {};
    for (const [key, raw] of [
      ['reorder_level_ml', reorderLevel],
      ['reorder_qty_ml', reorderQty],
    ] as const) {
      if (raw.trim() === '') continue;
      const value = Number(raw);
      if (!Number.isFinite(value) || value <= 0) {
        setActionError('Both reorder values must be greater than 0');
        return;
      }
      settings[key] = value;
    }
    // The shelf number is only sent when it actually changed — an untouched
    // field must not be part of the update, or saving a threshold would keep
    // re-asserting a number and could collide with a renumbering done elsewhere.
    const currentCode =
      reorderItem.manufacturer_code != null ? String(reorderItem.manufacturer_code) : '';
    if (reorderCode.trim() !== currentCode) {
      if (reorderCode.trim() === '') {
        settings.manufacturer_code = null;
      } else {
        const code = Number(reorderCode);
        if (!Number.isInteger(code) || code <= 0) {
          setCodeError('Enter a whole number greater than 0, or leave it blank');
          return;
        }
        settings.manufacturer_code = code;
      }
    }

    if (Object.keys(settings).length === 0) {
      setActionError('Enter a low-stock alert level, a reorder quantity or a manufacturer code');
      return;
    }

    setSavingReorder(true);
    setActionError('');
    setCodeError('');
    setNotice('');
    try {
      const { item, warning } = await inventoryApi.updateReorderSettings(
        reorderItem.oil_name,
        settings
      );
      // Patch the row in place from the server's copy — a full reload would
      // collapse any expanded batch list the admin was reading.
      if (item) {
        setItems((prev) => prev.map((i) => (i.oil_name === item.oil_name ? item : i)));
      }
      if (warning) setNotice(warning);
      setReorderItem(null);

      // A threshold raised past current stock opens a low-stock alert server
      // side, so the alerts card below is now stale. Refreshed on its own and
      // swallowed — a stale alert list must not turn a saved setting into an
      // error message.
      try {
        setAlerts(await inventoryApi.getAlerts());
      } catch {
        /* keep the alerts we already have */
      }
    } catch (err) {
      // 409 is the one failure that belongs next to the field that caused it:
      // the server's message names the oil already holding the number, and the
      // fix is to type a different one without leaving the modal.
      if ((err as any)?.response?.status === 409) {
        setCodeError(apiErrorMessage(err));
      } else {
        setActionError(apiErrorMessage(err));
      }
    } finally {
      setSavingReorder(false);
    }
  };

  const handleResolve = async (id: string) => {
    setResolvingId(id);
    setActionError('');
    try {
      await inventoryApi.resolveAlert(id);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setResolvingId('');
    }
  };

  // A number in the search box means the shelf number, not a substring of it:
  // typing 31 shows oil 31, never 131 or 310. Anything else matches the name.
  const query = search.trim();
  const searchCode = /^#?\d+$/.test(query) ? parseInt(query.replace(/^#/, ''), 10) : null;
  const visible = items.filter((item) =>
    searchCode !== null
      ? item.manufacturer_code === searchCode
      : item.oil_name.toLowerCase().includes(query.toLowerCase())
  );

  const sorted = [...visible].sort((a, b) => {
    const diff = codeOf(a) - codeOf(b);
    if (diff !== 0) return codeSortAsc ? diff : -diff;
    // Unnumbered oils all share the same sort key, so the name decides between
    // them — otherwise their order would drift between renders.
    return a.oil_name.localeCompare(b.oil_name);
  });

  // Counts stay whole-inventory numbers: they answer "how much of my stock is
  // low", which a search box must not quietly re-scope.
  const counts = {
    total: items.length,
    ok: items.filter((i) => i.status === 'ok').length,
    low: items.filter((i) => i.status === 'low').length,
    out: items.filter((i) => i.status === 'out').length,
  };
  const outAlerts = alerts.filter((a) => a.alert_type === 'out_of_stock');

  // oil name → number of customers still waiting on it.
  const waitingByOil = new Map(
    waiting.filter((w) => w.subject_type === 'oil').map((w) => [w.subject_id, w.waiting_count])
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Inventory</h1>
        <p className="text-sm text-sub">Oil stock, alerts and transactions</p>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {actionError && <ErrorBanner message={actionError} />}

      {/* Saved-but-worth-knowing: the setting went through, it just has a
          consequence the admin should see. Dismissible, not an error. */}
      {notice && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-sm text-warn">
          <div className="flex items-start gap-3">
            <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{notice}</p>
          </div>
          <button className="shrink-0 text-xs underline" onClick={() => setNotice('')}>
            Dismiss
          </button>
        </div>
      )}

      {/* Alert banner */}
      {alerts.length > 0 && (
        <div
          className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${
            outAlerts.length > 0
              ? 'border-bad/40 bg-bad/10 text-bad'
              : 'border-warn/40 bg-warn/10 text-warn'
          }`}
        >
          <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {outAlerts.length > 0 && (
              <span className="font-semibold">
                {outAlerts.length} oil{outAlerts.length === 1 ? '' : 's'} out of stock.{' '}
              </span>
            )}
            {alerts.length} unresolved alert{alerts.length === 1 ? '' : 's'} — see below.
          </p>
        </div>
      )}

      {/* Summary cards */}
      {loading ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Total oils</p>
            <p className="mt-2 text-2xl font-semibold">{counts.total}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Healthy stock</p>
            <p className="mt-2 text-2xl font-semibold text-ok">{counts.ok}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Low stock</p>
            <p className="mt-2 text-2xl font-semibold text-warn">{counts.low}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Out of stock</p>
            <p className="mt-2 text-2xl font-semibold text-bad">{counts.out}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-edge">
        {(['stock', 'transactions'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t ? 'border-gold text-gold' : 'border-transparent text-sub hover:text-ink'
            }`}
          >
            {capitalize(t)}
          </button>
        ))}
      </div>

      {tab === 'stock' ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-[240px] flex-1">
              <input
                className="input w-full"
                type="search"
                placeholder="Search by name or shelf number (e.g. sand, 31)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {query !== '' && (
                <p className="mt-1.5 text-xs text-sub">
                  {sorted.length} of {items.length} oils
                  {searchCode !== null && <> · matching shelf number {searchCode}</>}
                </p>
              )}
            </div>
            <button
              className="btn-outline shrink-0"
              disabled={items.length === 0}
              onClick={() => {
                setActionError('');
                // Always the WHOLE list, never the current search: this sheet
                // goes on the bench next to the rack, where a filtered list is
                // worse than none.
                if (!printOilList(items)) {
                  setActionError('Could not open the print window — allow pop-ups for this site.');
                }
              }}
            >
              Print oil list
            </button>
          </div>

          <Card bodyClassName="!p-0">
            {loading ? (
              <TableSkeleton rows={8} />
            ) : sorted.length === 0 ? (
              <EmptyState
                message={items.length === 0 ? 'No inventory items' : `No oil matches "${query}"`}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-edge">
                      {/* Shelf number first, because that is the order the
                          bottles stand in and the order this table defaults to.
                          Click to flip: ascending walks the shelf, descending
                          brings the unnumbered oils — the ones needing a number
                          — to the top. */}
                      <th className="th !px-2 w-[60px] text-center">
                        <button
                          className="w-full text-center hover:text-ink"
                          title={
                            codeSortAsc
                              ? 'Sorted by shelf number, lowest first'
                              : 'Sorted by shelf number, highest first (unnumbered on top)'
                          }
                          onClick={() => setCodeSortAsc((asc) => !asc)}
                        >
                          # {codeSortAsc ? '↑' : '↓'}
                        </button>
                      </th>
                      <th className="th">Oil name</th>
                      <th className="th">Stock (ml)</th>
                      {/* Both reorder knobs are visible here so the current
                          setup can be read off the table without opening the
                          edit modal. */}
                      <th className="th">Alert at</th>
                      <th className="th">Reorder</th>
                      <th className="th">Cost/ml</th>
                      <th className="th">Status</th>
                      <th className="th">Last restocked</th>
                      <th className="th">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-edge">
                    {sorted.map((item) => (
                      <React.Fragment key={item.oil_name}>
                        <tr className="transition-colors hover:bg-edge/20">
                          <td className="td !px-2 text-center">
                            <OilCode code={item.manufacturer_code} />
                          </td>
                          <td className="td font-medium">
                            {item.oil_name}
                            {item.status !== 'ok' && (
                              <WaitingBadge count={waitingByOil.get(item.oil_name) ?? 0} />
                            )}
                          </td>
                          <td className="td">{formatMl(item.quantity_ml)}</td>
                          <td className="td text-sub">{formatMl(item.reorder_level_ml)}</td>
                          <td className="td text-sub">
                            {item.reorder_qty_ml != null ? formatMl(item.reorder_qty_ml) : '—'}
                          </td>
                          <td className="td">
                            {item.unit_cost_per_ml ? (
                              formatINRPrecise(item.unit_cost_per_ml)
                            ) : (
                              <span className="text-sub">—</span>
                            )}
                          </td>
                          <td className="td">
                            <StockStatus status={item.status} />
                          </td>
                          <td className="td text-sub">{formatDate(item.last_restocked)}</td>
                          <td className="td">
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                className="btn-outline !px-3 !py-1.5 !text-xs"
                                onClick={() => {
                                  setRestockItem(item);
                                  setRestockQty('');
                                  setRestockCost(
                                    item.unit_cost_per_ml != null ? String(item.unit_cost_per_ml) : ''
                                  );
                                  setRestockNotes('');
                                }}
                              >
                                Restock
                              </button>
                              <button
                                className="btn-outline !px-3 !py-1.5 !text-xs"
                                onClick={() => {
                                  setCostItem(item);
                                  setCostValue(
                                    item.unit_cost_per_ml != null ? String(item.unit_cost_per_ml) : ''
                                  );
                                }}
                              >
                                Edit cost
                              </button>
                              <button
                                className="btn-outline !px-3 !py-1.5 !text-xs"
                                onClick={() => {
                                  setReorderItem(item);
                                  setReorderLevel(String(item.reorder_level_ml));
                                  setReorderQty(
                                    item.reorder_qty_ml != null ? String(item.reorder_qty_ml) : ''
                                  );
                                  setReorderCode(
                                    item.manufacturer_code != null
                                      ? String(item.manufacturer_code)
                                      : ''
                                  );
                                  setCodeError('');
                                }}
                              >
                                Settings
                              </button>
                              <button
                                className="btn-outline !px-3 !py-1.5 !text-xs"
                                aria-expanded={expandedOil === item.oil_name}
                                onClick={() => toggleLayers(item.oil_name)}
                              >
                                {expandedOil === item.oil_name ? 'Hide batches' : 'View batches'}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Cost layers — the batches this oil's stock was bought
                            in, oldest first. Formula costing consumes them in
                            this order (FIFO), so the top rows are the ones being
                            drawn from now. */}
                        {expandedOil === item.oil_name && (
                          <tr className="bg-edge/10">
                            <td colSpan={9} className="px-4 py-4">
                              <CostLayers
                                layers={layersByOil[item.oil_name]}
                                loading={layersLoading === item.oil_name}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Alerts */}
          <Card title={`Unresolved alerts (${alerts.length})`} bodyClassName="!p-0">
            {alerts.length === 0 ? (
              <EmptyState message="No unresolved alerts" />
            ) : (
              <ul className="divide-y divide-edge">
                {alerts.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                    <div className="flex items-center gap-3 text-sm">
                      <IconAlert
                        className={`h-4 w-4 shrink-0 ${
                          a.alert_type === 'out_of_stock' ? 'text-bad' : 'text-warn'
                        }`}
                      />
                      <div>
                        <p className="font-medium">
                          {a.oil_name} —{' '}
                          {a.alert_type === 'out_of_stock' ? 'out of stock' : 'low stock'}
                        </p>
                        <p className="text-xs text-sub">
                          {formatMl(a.quantity_ml)} left (threshold {formatMl(a.threshold)}) ·{' '}
                          {formatDateTime(a.created_at)}
                        </p>
                      </div>
                    </div>
                    <button
                      className="btn-outline !px-3 !py-1.5 !text-xs"
                      disabled={resolvingId === a.id}
                      onClick={() => handleResolve(a.id)}
                    >
                      {resolvingId === a.id && <Spinner className="h-3 w-3" />}
                      Resolve
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      ) : (
        <Card title="Recent 50 transactions" bodyClassName="!p-0">
          {txLoading ? (
            <TableSkeleton rows={8} />
          ) : transactions.length === 0 ? (
            <EmptyState message="No transactions yet" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Date</th>
                    <th className="th">Oil</th>
                    <th className="th">Type</th>
                    <th className="th">Quantity</th>
                    <th className="th">Order ID</th>
                    <th className="th">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="transition-colors hover:bg-edge/20">
                      <td className="td text-sub">{formatDateTime(tx.created_at)}</td>
                      <td className="td font-medium">{tx.oil_name}</td>
                      <td className="td text-sub">{capitalize(tx.transaction_type)}</td>
                      <td className={`td font-medium ${tx.quantity_ml >= 0 ? 'text-ok' : 'text-bad'}`}>
                        {formatMlSigned(tx.quantity_ml)}
                      </td>
                      <td className="td text-sub">{tx.order_id ? shortId(tx.order_id) : '—'}</td>
                      <td className="max-w-[240px] truncate px-4 py-3 text-sm text-sub" title={tx.notes ?? ''}>
                        {tx.notes ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Restock modal */}
      <Modal
        open={restockItem !== null}
        title="Restock oil"
        onClose={() => setRestockItem(null)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setRestockItem(null)} disabled={restocking}>
              Cancel
            </button>
            <button
              className="btn-gold"
              onClick={handleRestock}
              disabled={
                restocking ||
                !restockQty ||
                Number(restockQty) <= 0 ||
                restockCost.trim() === '' ||
                Number(restockCost) < 0
              }
            >
              {restocking && <Spinner />}
              Confirm restock
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Oil name</label>
            <input className="input opacity-70" value={restockItem?.oil_name ?? ''} readOnly />
          </div>
          <div>
            <label className="label">Quantity to add (ml)</label>
            <input
              className="input"
              type="number"
              min={1}
              placeholder="e.g. 500"
              value={restockQty}
              onChange={(e) => setRestockQty(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Cost per ml for this batch (₹)</label>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              placeholder="e.g. 12.50"
              value={restockCost}
              onChange={(e) => setRestockCost(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-sub">
              What <em>this</em> batch cost. Stock bought earlier keeps its own price — formula
              costing consumes the oldest batch first.
            </p>
          </div>
          <div>
            <label className="label">Notes (optional)</label>
            <textarea
              className="input min-h-[64px]"
              placeholder="Supplier, batch, PO number…"
              value={restockNotes}
              onChange={(e) => setRestockNotes(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* Edit-cost modal */}
      <Modal
        open={costItem !== null}
        title="Edit cost per ml"
        onClose={() => setCostItem(null)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setCostItem(null)} disabled={savingCost}>
              Cancel
            </button>
            <button
              className="btn-gold"
              onClick={handleSaveCost}
              disabled={savingCost || costValue.trim() === '' || Number(costValue) < 0}
            >
              {savingCost && <Spinner />}
              Save cost
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Oil name</label>
            <input className="input opacity-70" value={costItem?.oil_name ?? ''} readOnly />
          </div>
          <div>
            <label className="label">Cost per ml (₹)</label>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              placeholder="e.g. 12.50"
              value={costValue}
              onChange={(e) => setCostValue(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-sub">
              Display price and the fallback for oils with no cost batches yet. Orders are costed
              from the batches (see “View batches”); use Restock to record a new one. Not shown to
              customers.
            </p>
          </div>
        </div>
      </Modal>

      {/* Reorder-settings modal */}
      <Modal
        open={reorderItem !== null}
        title={`Oil Settings — ${reorderItem?.oil_name ?? ''}`}
        onClose={() => {
          setReorderItem(null);
          setCodeError('');
        }}
        footer={
          <>
            <button
              className="btn-outline"
              onClick={() => {
                setReorderItem(null);
                setCodeError('');
              }}
              disabled={savingReorder}
            >
              Cancel
            </button>
            <button
              className="btn-gold"
              onClick={handleSaveReorder}
              disabled={
                savingReorder ||
                // Clearing the shelf number is a save on its own, so an empty
                // code field cannot count towards "nothing to save".
                (reorderLevel.trim() === '' &&
                  reorderQty.trim() === '' &&
                  reorderCode.trim() ===
                    (reorderItem?.manufacturer_code != null
                      ? String(reorderItem.manufacturer_code)
                      : '')) ||
                (reorderLevel.trim() !== '' && Number(reorderLevel) <= 0) ||
                (reorderQty.trim() !== '' && Number(reorderQty) <= 0)
              }
            >
              {savingReorder && <Spinner />}
              Save Settings
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Manufacturer Code</label>
            <input
              className={`input ${codeError ? '!border-bad' : ''}`}
              type="number"
              min={1}
              step={1}
              placeholder="e.g. 31"
              value={reorderCode}
              onChange={(e) => {
                setReorderCode(e.target.value);
                setCodeError('');
              }}
            />
            {codeError ? (
              <p className="mt-1.5 text-xs text-bad">{codeError}</p>
            ) : (
              <p className="mt-1.5 text-xs text-sub">
                Unique number assigned by the manufacturer — how this bottle is found on the
                shelf. Leave blank if not yet assigned.
              </p>
            )}
          </div>
          <div>
            <label className="label">Low Stock Alert (ml)</label>
            <input
              className="input"
              type="number"
              min={1}
              placeholder="e.g. 20"
              value={reorderLevel}
              onChange={(e) => setReorderLevel(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-sub">
              Show alert when stock drops to or below this level.
              {reorderItem && <> Current stock: {formatMl(reorderItem.quantity_ml)}.</>}
            </p>
          </div>
          <div>
            <label className="label">Suggested Reorder Quantity (ml)</label>
            <input
              className="input"
              type="number"
              min={1}
              placeholder="e.g. 50"
              value={reorderQty}
              onChange={(e) => setReorderQty(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-sub">
              How much to order from the supplier when restocking. Typical supplier bottle: 50 ml.
              A reminder only — nothing is ordered automatically.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
